import { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Send, MessageCircle } from 'lucide-react';

export default function GroupChat() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get('/referral_groups/messages?limit=50');
      setMessages(r.data.messages || []);
    } catch (_e) { /* not in group */ }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    if (t.length > 500) return toast.error('Max 500 characters');
    setSending(true);
    try {
      await api.post('/referral_groups/messages', { text: t });
      setText('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
    setSending(false);
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="mt-5 border border-[#2B3139] bg-[#0B0E11]" data-testid="group-chat">
      <div className="px-4 py-2.5 border-b border-[#2B3139] flex items-center gap-2">
        <MessageCircle className="w-4 h-4 text-[#FCD535]" />
        <span className="text-white text-sm font-semibold">Group Chat</span>
        <span className="text-[10px] uppercase text-[#5E6673] font-mono ml-1">live · updates every 10s</span>
      </div>
      <div ref={listRef} className="max-h-64 overflow-y-auto px-4 py-3 space-y-2" data-testid="chat-messages">
        {messages.length === 0 ? (
          <div className="text-center text-xs text-[#5E6673] italic py-6">
            No messages yet. Say hi to coordinate your referral push.
          </div>
        ) : messages.map(m => {
          const mine = m.user_id === user?.user_id;
          const isBot = m.user_id === 'system_bot' || m.is_system;
          if (isBot) {
            return (
              <div key={m.message_id} className="flex justify-center" data-testid={`bot-msg-${m.message_id}`}>
                <div className="px-3 py-1.5 text-xs text-center bg-[#FCD535]/10 border border-[#FCD535]/30 text-[#FCD535] max-w-[85%] rounded-sm">
                  {m.text}
                </div>
              </div>
            );
          }
          const rocketUsers = m.reactions?.["🚀"] || [];
          const gemUsers = m.reactions?.["💎"] || [];
          const iReactedRocket = rocketUsers.includes(user?.user_id);
          const iReactedGem = gemUsers.includes(user?.user_id);
          const toggle = async (emoji) => {
            try {
              const r = await api.post(`/referral_groups/messages/${m.message_id}/react`, { emoji });
              setMessages(prev => prev.map(x => x.message_id === m.message_id ? { ...x, reactions: r.data.reactions } : x));
            } catch (_e) { /* ignore */ }
          };
          return (
            <div key={m.message_id} className={`flex gap-2 ${mine ? 'flex-row-reverse' : ''}`}>
              {m.user_picture ? (
                <img src={m.user_picture} alt="" className="w-7 h-7 rounded-full flex-shrink-0" />
              ) : (
                <div className="w-7 h-7 rounded-full bg-[#2B3139] grid place-items-center text-[#EAECEF] text-xs font-semibold flex-shrink-0">
                  {m.user_name?.[0]?.toUpperCase() || '?'}
                </div>
              )}
              <div className={`flex flex-col ${mine ? 'items-end' : 'items-start'} max-w-[75%]`}>
                <div className="flex items-baseline gap-1.5">
                  <span className={`text-xs font-semibold ${mine ? 'text-[#FCD535]' : 'text-[#EAECEF]'}`}>{mine ? 'You' : m.user_name}</span>
                  <span className="text-[10px] text-[#5E6673] font-mono">{new Date(m.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
                <div className={`px-3 py-1.5 mt-0.5 text-sm text-[#EAECEF] whitespace-pre-wrap break-words ${mine ? 'bg-[#FCD535]/12 border border-[#FCD535]/30' : 'bg-[#181A20] border border-[#2B3139]'}`}>
                  {m.text}
                </div>
                <div className={`flex items-center gap-1 mt-1 ${mine ? 'flex-row-reverse' : ''}`} data-testid={`msg-reactions-${m.message_id}`}>
                  <button onClick={() => toggle("🚀")} data-testid={`react-rocket-${m.message_id}`}
                    className={`flex items-center gap-1 px-1.5 py-0.5 text-[11px] rounded border transition-colors ${iReactedRocket ? 'bg-[#FCD535]/15 border-[#FCD535]/40 text-[#FCD535]' : 'bg-[#0B0E11] border-[#2B3139] text-[#848E9C] hover:text-white'}`}>
                    🚀 {rocketUsers.length > 0 && <span className="font-mono">{rocketUsers.length}</span>}
                  </button>
                  <button onClick={() => toggle("💎")} data-testid={`react-gem-${m.message_id}`}
                    className={`flex items-center gap-1 px-1.5 py-0.5 text-[11px] rounded border transition-colors ${iReactedGem ? 'bg-[#0ECB81]/15 border-[#0ECB81]/40 text-[#0ECB81]' : 'bg-[#0B0E11] border-[#2B3139] text-[#848E9C] hover:text-white'}`}>
                    💎 {gemUsers.length > 0 && <span className="font-mono">{gemUsers.length}</span>}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="border-t border-[#2B3139] p-2 flex gap-2">
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKey}
          placeholder="Type a message… (Enter to send)"
          maxLength={500}
          className="bg-[#0B0E11] border-[#2B3139] rounded-sm text-white flex-1"
          data-testid="chat-input"
        />
        <Button onClick={send} disabled={sending || !text.trim()}
          className="bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] rounded-sm px-3"
          data-testid="chat-send-btn">
          <Send className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
